import type { CompanyFilters, RegionId } from "../../types";
import type { GeographyApproach, GeographySlice } from "./slice";
import { normalizeStringArray } from "../../utils/normalize";

function normalizeRegionArray(input?: Array<string | null> | null): RegionId[] {
  const values = normalizeStringArray(input);
  return values.filter(
    (v): v is RegionId => v === "AMER" || v === "EMEA" || v === "APAC"
  );
}

function removeOverlappingCountries(
  included: string[],
  excluded: string[]
): string[] {
  if (included.length === 0 || excluded.length === 0) return excluded;
  const includedSet = new Set(included);
  return excluded.filter((code) => !includedSet.has(code));
}

export function toCompanyFilters(
  slice: GeographySlice
): Partial<CompanyFilters> {
  const out: Partial<CompanyFilters> = {};

  if (slice.approach === "regions") {
    if (slice.regions.length > 0) out.regionNames = slice.regions;
    if (slice.excludedCountries.length > 0) {
      out.excludedCountryAlpha2s = slice.excludedCountries;
    }
    return out;
  }

  if (slice.approach === "countries") {
    if (slice.countries.length > 0) out.countryAlpha2s = slice.countries;
    if (slice.excludedCountries.length > 0) {
      out.excludedCountryAlpha2s = removeOverlappingCountries(
        slice.countries,
        slice.excludedCountries
      );
    }
    return out;
  }

  // states
  if (slice.countries.length === 1) {
    out.countryAlpha2s = slice.countries;
    if (slice.states.length > 0) out.stateIds = slice.states;
  }

  return out;
}

export function fromCompanyFilters(filters: CompanyFilters): GeographySlice {
  const countries = normalizeStringArray(filters.countryAlpha2s);
  const excludedCountries = normalizeStringArray(
    filters.excludedCountryAlpha2s
  );
  const states = normalizeStringArray(filters.stateIds).map(String);
  const regions = normalizeRegionArray(filters.regionNames);

  const approach: GeographyApproach =
    states.length > 0
      ? "states"
      : countries.length > 0
        ? "countries"
        : regions.length > 0
          ? "regions"
          : "regions";

  if (approach === "regions") {
    return {
      approach,
      regions,
      countries: [],
      states: [],
      excludedCountries,
    };
  }

  if (approach === "countries") {
    return {
      approach,
      regions: [],
      countries,
      states: [],
      excludedCountries: removeOverlappingCountries(
        countries,
        excludedCountries
      ),
    };
  }

  // states
  const [selectedCountry] = countries;
  return {
    approach,
    regions: [],
    countries: selectedCountry ? [selectedCountry] : [],
    states: selectedCountry ? states : [],
    excludedCountries: [],
  };
}
